/* AQUON shared quick-cart helper.
   Custom sections that add to the cart with their own fetch should call
   window.AquonCart.open() (or addAndOpen) afterwards so the theme's cart
   drawer refreshes and auto-opens - exactly like the native add-to-cart.

   Why this exists: the drawer (<cart-drawer-component auto-open>) only opens
   when it receives a `cart:update` event whose detail carries data.itemCount.
   A bare `cart:update` (or a redirect to /cart) never opens the drawer. */
(function () {
  'use strict';

  var origFetch = window.fetch.bind(window);

  /* Ask the cart drawer + count components which section ids to re-render. */
  function gatherGroupedSectionIds() {
    var sections = [];
    var evt = new Event('cart:grouped-sections', { bubbles: true });
    evt.detail = { sections: sections };
    try { document.dispatchEvent(evt); } catch (e) {}
    var seen = {};
    var out = [];
    sections.forEach(function (id) {
      if (id && !seen[id]) { seen[id] = true; out.push(id); }
    });
    return out;
  }

  function fetchSectionsHtml(ids) {
    if (!ids.length) return Promise.resolve({});
    var basePath = window.location.pathname.split('?')[0];
    return Promise.all(ids.map(function (id) {
      return origFetch(basePath + '?section_id=' + encodeURIComponent(id), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { return [id, html]; })
        .catch(function () { return [id, '']; });
    })).then(function (pairs) {
      var out = {};
      pairs.forEach(function (p) { if (p[1]) out[p[0]] = p[1]; });
      return out;
    });
  }

  /* Opens the drawer only when the theme is configured to pop it open after an
     add. `auto-open` is emitted by sections/cart-drawer.liquid from the
     `cart_auto_open` setting and is the same flag the theme's own cart-update
     handler checks, so one switch governs both paths. With it off the cart is
     still refreshed - the customer just is not interrupted. The header cart
     icon calls CartDrawerComponent.open() directly and always works. */
  function forceOpenDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && !drawer.hasAttribute('auto-open')) return;
    if (drawer && typeof drawer.showDialog === 'function') {
      var dlg = drawer.querySelector('[ref="dialog"], dialog');
      if (!dlg || !dlg.hasAttribute('open')) {
        try { drawer.showDialog(); } catch (e) {}
      }
    }
  }

  function cartUpdateEvent(cart, sections) {
    var evt = new Event('cart:update', { bubbles: true });
    evt.detail = {
      resource: cart,
      sourceId: 'aquon-quick-cart',
      data: {
        source: 'aquon-quick-cart',
        itemCount: (cart && cart.item_count) || 0,
        sections: Object.keys(sections).length ? sections : undefined,
        isIncremental: false
      }
    };
    return evt;
  }

  /* Refresh the cart drawer + count from the given cart object and open the drawer. */
  function refreshAndOpen(cart) {
    var ids = gatherGroupedSectionIds();
    return fetchSectionsHtml(ids).then(function (sections) {
      try { document.dispatchEvent(cartUpdateEvent(cart, sections)); } catch (e) {}
      /* The event's `sections` let the drawer morph its own content natively;
         we only ensure the drawer is actually open. */
      forceOpenDrawer();
    });
  }

  /* Same refresh, but the drawer stays shut - for surfaces that should add to
     the cart and leave the customer exactly where they are (the PDP sticky bar).
     The count bubble and the drawer both read `itemCount` off this event, so the
     payload cannot simply omit it; instead the drawer's own `auto-open`
     attribute (its documented opt-out) is lifted for the dispatch. The dispatch
     is synchronous, so there is no window in which a genuine add could be
     missed. Cart count, drawer contents, free-shipping goal and every other
     cart:update listener still update normally. */
  function refreshQuiet(cart) {
    var ids = gatherGroupedSectionIds();
    return fetchSectionsHtml(ids).then(function (sections) {
      var drawers = document.querySelectorAll('cart-drawer-component[auto-open]');
      drawers.forEach(function (d) { d.removeAttribute('auto-open'); });
      try { document.dispatchEvent(cartUpdateEvent(cart, sections)); } catch (e) {}
      drawers.forEach(function (d) { d.setAttribute('auto-open', ''); });
    });
  }

  /* Refresh the cart surfaces without opening the drawer. */
  function refresh() {
    return origFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(refreshQuiet)
      .catch(function () {});
  }

  /* Open the drawer immediately for snappy UX, then refresh its contents in the
     background. */
  function open() {
    forceOpenDrawer();
    return origFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(refreshAndOpen)
      .catch(function () {});
  }

  /* Add items to the cart, then refresh + open the drawer.
     Returns a promise resolving to the /cart/add.js response, or rejecting
     with the error payload so callers can show their own error message. */
  function addAndOpen(items) {
    return origFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) return Promise.reject(res.data);
        // Reconcile the free gift BEFORE the drawer renders so it opens straight
        // to the final state (correct gift + tier), never a mid-swap flash. This
        // add used the original fetch, so the engine's interceptor didn't run.
        var reconciled = (window.AquonBundleEngine && window.AquonBundleEngine.reconcile)
          ? window.AquonBundleEngine.reconcile().catch(function () {})
          : Promise.resolve();
        return reconciled.then(open).then(function () { return res.data; });
      });
  }

  window.AquonCart = {
    refreshAndOpen: refreshAndOpen,
    open: open,
    addAndOpen: addAndOpen,
    refresh: refresh,
    refreshQuiet: refreshQuiet
  };
})();
